import h from 'virtual-dom/h';
import {DefaultSettings, settings} from '../../../settings/settings-util';
import {AnyAction, Reducer, Store} from "redux";
import {AlmeState, defaultState} from "../../../wireup/AlmeState";
import {SingleLinkRenderer} from "./models/SingleLinkRenderer";
import {HistoryMessage, HistoryMessageAlme} from "../../../types/history";
import {VChild} from "virtual-dom";
import {LinkSpec} from "../../../settings/button-collection";
import {AlmeUIInput} from "../../../types/input";
import {sendRequest} from "../../lib-alme-request/src/lib-alme-request";
import {ButtonType, CallToActionResponseAction} from "./models/CallToActionResponseActions";
import {Dictionary, Mutator} from "../../../types/rendering";
import {CallToActionButtonActionType} from "./actions/Actions";
import {VNode} from "virtual-dom";
import {
    isLiveChat,
} from '../../lib-livechat/lib/lib-livechat';

type StepOutIcon = { renderIcon: (url: string) => VNode | undefined };
type MutatorModule = { getMutator: () => Mutator };

export default class CallToActionButtonMessageRenderer
{
    _settings: DefaultSettings;
    _store: Store<AlmeState> | null = null;
    _singleLinkRenderer: SingleLinkRenderer;
    _mutatorModules: MutatorModule[];
    _stepOutIcon: StepOutIcon | null;
    _mutators: Mutator[] = [];


    //#region Public Interface

    constructor(passedSettings: DefaultSettings | null, singleLinkRenderer: SingleLinkRenderer, stepOutIcon: StepOutIcon | null, mutatorModules: MutatorModule[] = [])
    {
        this._settings = passedSettings ?? settings;
        this._stepOutIcon = stepOutIcon;
        this._singleLinkRenderer = singleLinkRenderer;
        this._mutatorModules = mutatorModules;
    }

    static getDefaultSettings(): DefaultSettings
    {
        return settings;
    }

    initialize(store): void
    {
        this._store = store;
        this._mutators = this._mutatorModules.reduce((mutators, module) => [...mutators, module.getMutator()], [] as Mutator[]);
    }

    getReducer(): Reducer<AlmeState["Window"]>
    {
        return (state = defaultState.Window, action: AnyAction): AlmeState["Window"] =>
        {
            let newShouldDisable;

            switch (action.type)
            {
                case "DISPLAY_CALL_TO_ACTION_BUTTONS":
                    return {
                        ...state,
                        CallToActionButtons: {
                            ...state.CallToActionButtons,
                            shouldDisable: {
                                ...state.CallToActionButtons.shouldDisable,
                                [(action as CallToActionButtonActionType).entryId]: false,
                            },
                        },
                    };

                case "DISABLE_CALL_TO_ACTION_BUTTONS":
                    newShouldDisable = { ...state.CallToActionButtons.shouldDisable };
                    delete newShouldDisable[(action as CallToActionButtonActionType).entryId];

                    return {
                        ...state,
                        CallToActionButtons: {
                            shouldDisable: newShouldDisable,
                        },
                    };

                default:
                    return state;
            }
        };
    }


    getRenderer(): (message: HistoryMessage) => VChild
    {
        return (message: HistoryMessage): VChild =>
        {
            const { callToActionResponseActionName } = this._settings.responseActionConstants;
            const almeMessage = message as HistoryMessageAlme;
            const responseActions = almeMessage?.responseActions?.filter(action => action.Name === callToActionResponseActionName);
            const entryId = almeMessage?.extensionData.entryId as string;

            if (!responseActions?.length || !entryId) { return []; }

            const childNodes = responseActions.map(responseAction => {
                const ctaResponseAction = responseAction as CallToActionResponseAction;

                let stepOutNode ;
                if(this._stepOutIcon?.length == 0)
                {
                    stepOutNode = null;
                }
                else
                {
                 stepOutNode = this._stepOutIcon?.renderIcon(ctaResponseAction.ButtonTarget as string);
                }
                const renderedLink = this._singleLinkRenderer.renderLink(this.buildLinkSpec(ctaResponseAction, entryId, stepOutNode));

                const aLink = this.isAppEvent(ctaResponseAction.ButtonType) ? renderedLink : renderedLink.children[0];

                return h('div', { className: this._settings.classNames.callToAction }, [aLink]);
            });

            return h('div', { className: this._settings.classNames.callToActionWrapper, 
                style: {
                    marginTop: '15px',
                }
            }, childNodes);
        };
    }

    //#endregion



    //#region Build Methods

    buildLinkSpec(config: CallToActionResponseAction, entryId: string, stepOutNode:VNode): LinkSpec
    {
        const shouldDisable = this.shouldDisable(entryId);
        const connectionStatus = isLiveChat(this._store, this._settings);
        const {callToActionButtonDisabled, callToActionButton} = this._settings.classNames;

        let linkSpec = {
            tag: "a",
            TargetType: config.ButtonType,
            Metadata: this.buildLinkSpecMetadata(config),
            AppeventName: this.isAppEvent(config.ButtonType) ? config.ButtonTarget : "",
            PreAlme: this.buildPreAlmeFunction(config),
            DisplayText: stepOutNode ? [config.ButtonDisplayText, stepOutNode] :config.ButtonDisplayText,
            parameters: Object.assign({ entryId }, config.parameters),
            attributes: {
                "type": "a",
                "title": config.ButtonDisplayText,
                'aria-label': config.ButtonDisplayText,
            },
            className: shouldDisable ? callToActionButtonDisabled : (!connectionStatus) ? callToActionButton : callToActionButtonDisabled,
            disabled: shouldDisable && (!connectionStatus),
        } as LinkSpec;

        if (entryId)
        {
            linkSpec.id = `nit-call-to-action-${entryId}`;
        }

        // run mutators to update properties
        linkSpec = this._mutators.reduce((props, mutate) => mutate(props), linkSpec as unknown as Dictionary) as unknown as LinkSpec;

        return linkSpec;
    }

    buildLinkSpecMetadata(config: CallToActionResponseAction): Dictionary
    {
        switch (config.ButtonType)
        {
            case "Appevent":
                return { };

            case "Input":
                return { InputText: config.ButtonTarget };

            case "Unit":
                return { UnitUID: config.ButtonTarget };

            case "Url":
                return {
                    NavigationType: config.ButtonMetadata,
                    UrlAddress: config.ButtonTarget,
                };
        }
    }

    buildPreAlmeFunction(config: CallToActionResponseAction): (e: unknown) => void
    {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        let preAlmeFunction = (e?: unknown): void => undefined;
        const settings = this._settings.CallToActionButton;

        if (settings && typeof settings[config.ButtonType]?.PreAlme === 'function')
        {
            preAlmeFunction = settings[config.ButtonType]?.PreAlme;
        }

        return (e): void => {
            if(config.ReportingEventName){
                const input = {
                    eventName: config.ReportingEventName ?? "",
                    eventValue: config.ReportingEventValue ?? "",
                    messageType: this._settings.messageConstants.reportingEventRequest,
                } as AlmeUIInput;
                sendRequest(input);
            }
            preAlmeFunction(e);
        };
    }

    //#endregion



    //#region Helper Methods

    isAppEvent(targetType: ButtonType): boolean
    {
        return targetType === "Appevent";
    }

    shouldDisable(entryId: string): boolean
    {
        const { Base, CallToActionButton } = this._settings.stateLocations.Window;

        return this._store?.getState()[Base][CallToActionButton.Base][CallToActionButton.shouldDisable][entryId] ?? true;
    }

    //#endregion
}



// WEBPACK FOOTER //
// ./node_modules/@alme/ui-web/modules/message-renderer-call-to-action/src/message-renderer-call-to-action.ts